/**
 * Family captured by the Figma plugin for `figma.mixed` font names
 * (`map-text.ts` `extractFontFamily` fallback). It is a generic CSS keyword, not
 * a real licensed face, so it can never resolve against an uploaded font — we
 * drop it from the usage set so P03's "missing fonts" UX never asks the agency
 * to upload `sans-serif` and the resolver never tries to match it.
 */
const GENERIC_FALLBACK_FAMILY = 'sans-serif';
/**
 * Collect the distinct (family, weight) set used by a definition's text.
 *
 * Each text element contributes its element-level (family, weight). Mixed-style
 * text additionally contributes (family, runWeight) for every run that overrides
 * the weight — runs carry no family of their own, so they inherit the element's.
 * The result is de-duplicated and order-stable (first appearance wins).
 */
export function collectFontUsage(definition) {
    const seen = new Set();
    const usage = [];
    const add = (family, weight) => {
        // Tolerate a text element with no family (partial/in-progress definitions in
        // the browser, e.g. `freeFacesForDefinition` running live) — skip it, exactly
        // like an empty family, rather than throwing on `.trim()` (Sprint 049).
        const fam = family?.trim();
        if (!fam)
            return;
        // Skip the `figma.mixed` generic fallback — not an uploadable real face.
        if (fam.toLowerCase() === GENERIC_FALLBACK_FAMILY)
            return;
        // NUL separates the two fields so they cannot collide across the boundary
        // (e.g. family "A" + weight 1700 vs family "A1" + weight 700).
        const key = `${fam}\u0000${weight}`;
        if (seen.has(key))
            return;
        seen.add(key);
        usage.push({ fontFamily: fam, fontWeight: weight });
    };
    for (const el of definition.elements) {
        if (el.type !== 'text' || !el.text)
            continue;
        const t = el.text;
        add(t.fontFamily, t.fontWeight);
        if (t.runs) {
            for (const run of t.runs) {
                if (run.fontWeight !== undefined)
                    add(t.fontFamily, run.fontWeight);
            }
        }
    }
    return usage;
}
